import classNames from 'classnames'
import React, { MouseEvent, useMemo, useRef, forwardRef } from 'react'
import { Button as BaseComponent, ButtonProps as BaseProps, Popconfirm, PopconfirmProps } from 'antd'
import { useSafeState, useMemoizedFn } from 'ahooks'
import { Merge } from '@/utils/utilityTypes'
import styles from './index.less'

const INIT_LOADING_DELAY = 300

const DEFAULT_CONFIRM_PROPS: PopconfirmProps = {
    title: '确认执行此操作吗',
    placement: 'top',
    trigger: [],
    okText: '确定',
    cancelText: '取消',
    autoAdjustOverflow: true,
    arrowPointAtCenter: true
}

interface ButtonExtendProps {
    /**
     * 按钮类型
     *
     * - 大部分类型与 antd 样式和功能均一致，直接参考 antd，这里只列举定制过的类型；
     * - `primary-outline`：仅轮廓使用主题色，背景仍为默认的白色
     * - `link-compact`：紧凑型的链接样式（没有边框和边距）
     *
     * @default default
     */
    type?: BaseProps['type'] | 'primary-outline' | 'link-compact'
    /** 覆盖默认的 `onClick` 函数，支持接收异步函数并自动处理加载态 */
    onClick?: (e?: MouseEvent<HTMLElement>) => Promise<any> | any
    /**
     * 点击后是否需要二次确认
     *
     * - `undefined`、`false` 不传或传入非真值表示不需要二次确认
     * - `true` 表示需要二次确认
     * - `object` 表示需要二次确认，并传入自定义的确认框属性
     *
     * @default false
     */
    confirm?: boolean | PopconfirmProps
}

export type ButtonProps = Merge<BaseProps, ButtonExtendProps>

export const Button = forwardRef<HTMLDivElement, ButtonProps>((props: ButtonProps, ref) => {
    const { type, className, loading, onClick, confirm, ...restProps } = props

    const confirmEnabled = !!confirm
    const loading$ = useRef<boolean>(false)
    const [innerLoading, setInnerLoading] = useSafeState<boolean>(false)

    const btnType = ((): BaseProps['type'] => {
        if (type === 'primary-outline') {
            return 'default'
        }

        if (type === 'link-compact') {
            return 'link'
        }

        return type
    })()

    const mergedClassName = classNames(
        {
            [styles.primaryOutline]: type === 'primary-outline',
            [styles.linkCompact]: type === 'link-compact'
        },
        className
    )

    const mergedLoading = ((): ButtonProps['loading'] => {
        // 外部传入的 `loading` 优先级最高
        if (typeof loading === 'boolean') {
            return loading
        }
        // 内部 `innerLoading` 生效时增加延迟，短时间 loading 不转圈
        return innerLoading ? { delay: INIT_LOADING_DELAY } : false
    })()

    const changeLoadingState = (loading: boolean) => {
        if (typeof onClick !== 'function' && !loading$.current) {
            return
        }

        loading$.current = loading
        setInnerLoading(loading)
    }

    const handleSubmit = useMemoizedFn(async (e?: MouseEvent<HTMLElement>) => {
        if (loading$.current) {
            return
        }

        changeLoadingState(true)

        try {
            await onClick?.(e)
        } finally {
            changeLoadingState(false)
        }
    })

    const handleClick = (e: MouseEvent<HTMLElement>) => {
        if (confirmEnabled) {
            return
        }

        handleSubmit(e)
    }

    const mergedConfirmProps = useMemo<PopconfirmProps>(() => {
        const customConfirmProps: Partial<PopconfirmProps> = typeof confirm === 'object' ? confirm : {}
        return {
            ...DEFAULT_CONFIRM_PROPS,
            trigger: confirmEnabled ? customConfirmProps.trigger || 'click' : [],
            onConfirm: e => {
                handleSubmit(e)
            },
            ...customConfirmProps
        }
    }, [confirm, confirmEnabled, handleSubmit])

    const button = (
        <BaseComponent
            ref={ref}
            type={btnType}
            className={mergedClassName}
            loading={mergedLoading}
            onClick={handleClick}
            {...restProps}
        />
    )

    if (confirm) {
        // Popconfirm 会在按钮上多包装一层 span 元素，所以只能在需要的时候再使用 Popconfirm 包装按钮组件
        return <Popconfirm {...mergedConfirmProps}>{button}</Popconfirm>
    }

    return button
})
